import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Cpu as CpuIcon,
  FileText as FileTextIcon,
  Radio as RadioIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  collectAdditionalProperties,
  formatSilLevel,
  protectiveFunctionTypeLabel,
  useGetPlantProtectiveFunctionDetailQuery,
  type PlantEquipment,
  type PlantFunctionLogicDiagram,
  type PlantProtectiveFunction,
  type PlantProtectiveFunctionDetail,
  type PlantProtectiveFunctionDevice,
  type PlantSafetyPoint
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { plantSafetyIndexHref, plantSrsFunctionGroupHref } from '../../lib/plant-links'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { SectionHeader } from './entity/SectionHeader'
import { PlantHref } from './PlantHref'
import { SilRequirementBadge } from './SilRequirementBadge'

/**
 * `/plant/safety/:functionId` — single protective-function detail.
 *
 * Renders SRS identity (type, SIL, demand), the owning line/site and
 * logic solver, then the sensor → final-element chain and the process
 * equipment the function protects.
 */
const ProtectiveFunctionDetailPage = (): React.ReactElement => {
  const { functionId = '' } = useParams<{ functionId: string }>()
  const decodedFunctionId = decodeRouteParam(functionId)

  const { data, isLoading, isError } = useGetPlantProtectiveFunctionDetailQuery(decodedFunctionId, {
    skip: !decodedFunctionId
  })

  const fn = data?.function
  const requiredSilLabel = formatSilLevel(fn?.required_sil)
  const achievedSilLabel = formatSilLevel(fn?.sil)
  const sensors = React.useMemo(() => data?.sensors ?? [], [data?.sensors])
  const finalElements = React.useMemo(() => data?.finalElements ?? [], [data?.finalElements])
  const protectedEquipment = React.useMemo(
    () => data?.protectedEquipment ?? [],
    [data?.protectedEquipment]
  )
  const implementedDiagrams = React.useMemo(
    () => data?.implementedDiagrams ?? [],
    [data?.implementedDiagrams]
  )
  const implementedPoints = React.useMemo(
    () => data?.implementedPoints ?? [],
    [data?.implementedPoints]
  )
  const additionalProperties = React.useMemo(
    () =>
      fn
        ? collectAdditionalProperties(
            'ProtectiveFunction',
            fn as unknown as Record<string, unknown>
          )
        : [],
    [fn]
  )

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantSafetyIndexHref()} className="hover:text-ink hover:underline">
          Safety
        </Link>
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{fn?.name ?? decodedFunctionId}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this protective function"
          hint="The function may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading protective function…" hint="" muted />
      ) : (
        <>
          <header className="flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <ShieldIcon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
                  {fn?.name ?? decodedFunctionId}
                </h1>
                {fn?.function_type && (
                  <Badge variant="secondary">{protectiveFunctionTypeLabel(fn.function_type)}</Badge>
                )}
                {requiredSilLabel && <Badge variant="outline">Required {requiredSilLabel}</Badge>}
                {achievedSilLabel && <Badge variant="outline">Achieved {achievedSilLabel}</Badge>}
                <SilRequirementBadge requiredSil={fn?.required_sil} achievedSil={fn?.sil} />
              </div>
              <p className="m-0 mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-ink-3">
                {data.line && (
                  <PlantHref
                    id={data.line.id}
                    label="Line"
                    node={data.line}
                    className="hover:text-ink hover:underline"
                  >
                    {data.line.name}
                  </PlantHref>
                )}
                {data.line && data.site && <span className="text-ink-4">on</span>}
                {data.site && (
                  <PlantHref
                    id={data.site.id}
                    label="Site"
                    node={data.site}
                    className="hover:text-ink hover:underline"
                  >
                    {data.site.name}
                  </PlantHref>
                )}
                {data.solver && (
                  <>
                    <span className="text-ink-4">·</span>
                    <CpuIcon size={14} aria-hidden />
                    <PlantHref
                      id={data.solver.id}
                      label="LogicSolver"
                      node={data.solver}
                      className="hover:text-ink hover:underline"
                    >
                      {data.solver.name}
                    </PlantHref>
                  </>
                )}
              </p>
              {fn?.description && <p className="m-0 mt-3 text-sm text-ink-2">{fn.description}</p>}
            </div>
          </header>

          <FunctionGroupingSection section={data.section} />

          {(fn?.purpose || fn?.scenario || fn?.ipl_description) && (
            <section>
              <SectionHeader label="Purpose" />
              <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
                {fn?.purpose && <p className="m-0 text-sm text-ink-2">{fn.purpose}</p>}
                {fn?.scenario && (
                  <p className="m-0 mt-2 text-sm text-ink-3">
                    <span className="font-medium text-ink-2">Scenario. </span>
                    {fn.scenario}
                  </p>
                )}
                {fn?.ipl_description && (
                  <p className="m-0 mt-2 text-sm text-ink-3">{fn.ipl_description}</p>
                )}
              </div>
            </section>
          )}

          <SpecsSection fn={fn} />

          <DeviceSection
            label="Sensors"
            emptyTitle="No sensors"
            emptyHint="This function has no SENSED_BY equipment."
            devices={sensors}
            columns={SENSOR_COLUMNS}
          />
          <DeviceSection
            label="Final elements"
            emptyTitle="No final elements"
            emptyHint="This function has no ACTUATES equipment."
            devices={finalElements}
            columns={ACTUATES_COLUMNS}
          />
          <ProtectedSection equipment={protectedEquipment} />
          <ImplementedBySection diagrams={implementedDiagrams} points={implementedPoints} />

          {fn && <EntityDetailsInspector entity={fn} additionalProperties={additionalProperties} />}
        </>
      )}
    </div>
  )
}

const SENSOR_COLUMNS: Array<[string, keyof PlantProtectiveFunctionDevice]> = [
  ['Group', 'group'],
  ['Voting', 'voting'],
  ['Trip setpoint', 'trip_setpoint'],
  ['Range', 'range'],
  ['Type', 'sensor_type']
]

const ACTUATES_COLUMNS: Array<[string, keyof PlantProtectiveFunctionDevice]> = [
  ['Group', 'group'],
  ['Voting', 'voting'],
  ['Action', 'action'],
  ['Stroke', 'stroke'],
  ['Fail design', 'fail_design']
]

function FunctionGroupingSection({
  section
}: {
  section: PlantProtectiveFunctionDetail['section']
}): React.ReactElement | null {
  const fields = [
    ['SRS function group', section?.function_group],
    ['SRS section category', section?.function_category]
  ].filter((field): field is [string, string] => Boolean(field[1]))
  if (fields.length === 0) return null

  return (
    <section>
      <SectionHeader label="SRS function grouping" />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {fields.map(([label, value]) => (
            <div key={label}>
              <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
                {label}
              </div>
              <div className="mt-0.5 text-13 font-medium text-ink">
                {label === 'SRS function group' && section ? (
                  <Link
                    to={plantSrsFunctionGroupHref(section.id)}
                    className="inline-flex items-center gap-1 hover:underline"
                  >
                    {value}
                    <span aria-hidden>→</span>
                  </Link>
                ) : (
                  value
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function DeviceSection({
  label,
  emptyTitle,
  emptyHint,
  devices,
  columns
}: {
  label: string
  emptyTitle: string
  emptyHint: string
  devices: PlantProtectiveFunctionDevice[]
  columns: Array<[string, keyof PlantProtectiveFunctionDevice]>
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label={label}
        subtitle={`${devices.length} ${devices.length === 1 ? 'device' : 'devices'}`}
      />
      {devices.length === 0 ? (
        <EmptyCard title={emptyTitle} hint={emptyHint} />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b bg-bg-sunk/60 text-11 font-semibold uppercase tracking-[0.06em] text-ink-3">
              <tr>
                <th className="px-3 py-2 font-semibold">Tag</th>
                {columns.map(([heading]) => (
                  <th key={heading} className="px-3 py-2 font-semibold">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {devices.map((device) => (
                <tr key={`${device.equipment.id}|${device.group ?? ''}`}>
                  <td className="px-3 py-2">
                    <PlantHref
                      id={device.equipment.id}
                      label="Equipment"
                      node={device.equipment}
                      className="font-medium text-ink hover:underline"
                    >
                      {device.equipment.name}
                    </PlantHref>
                  </td>
                  {columns.map(([heading, key]) => (
                    <td key={heading} className="px-3 py-2 text-ink-2">
                      {deviceString(device, key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function deviceString(
  device: PlantProtectiveFunctionDevice,
  key: keyof PlantProtectiveFunctionDevice
): React.ReactNode {
  const value = device[key]
  if (typeof value === 'string' && value) return value
  return <span className="text-ink-4">—</span>
}

function ImplementedBySection({
  diagrams,
  points
}: {
  diagrams: PlantFunctionLogicDiagram[]
  points: PlantSafetyPoint[]
}): React.ReactElement {
  const total = diagrams.length + points.length
  return (
    <section>
      <SectionHeader
        label="Safety Manager implementation"
        subtitle={total > 0 ? `${total} ${total === 1 ? 'link' : 'links'}` : undefined}
      />
      {total === 0 ? (
        <EmptyCard
          title="No Safety Manager links"
          hint="After SRS and Safety Manager are ingested, run the Safety Manager ↔ SRS mapping job to link this function to FLDs and points."
        />
      ) : (
        <div className="flex flex-col gap-2">
          {diagrams.map((diagram) => (
            <PlantHref
              key={diagram.id}
              id={diagram.id}
              label="FunctionLogicDiagram"
              node={diagram}
              className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
                <FileTextIcon size={16} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">
                  {diagram.name}
                </span>
                <span className="block truncate text-11 text-ink-3">
                  Function logic diagram
                  {diagram.fld_number ? ` · FLD ${diagram.fld_number}` : ''}
                </span>
              </span>
            </PlantHref>
          ))}
          {points.map((point) => (
            <PlantHref
              key={point.id}
              id={point.id}
              label="SafetyPoint"
              node={point}
              className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
                <RadioIcon size={16} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">{point.name}</span>
                <span className="block truncate text-11 text-ink-3">
                  Safety point
                  {point.point_type ? ` · ${point.point_type}` : ''}
                  {point.is_internal ? ' · internal' : ''}
                </span>
              </span>
            </PlantHref>
          ))}
        </div>
      )}
    </section>
  )
}

function ProtectedSection({ equipment }: { equipment: PlantEquipment[] }): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Protects"
        subtitle={`${equipment.length} ${equipment.length === 1 ? 'asset' : 'assets'}`}
      />
      {equipment.length === 0 ? (
        <EmptyCard
          title="No protected equipment"
          hint="This function has no PROTECTS edge to a process asset."
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {equipment.map((item) => (
            <PlantHref
              key={item.id}
              id={item.id}
              label="Equipment"
              node={item}
              className="inline-flex items-center rounded-md border bg-card px-2.5 py-1 text-sm font-medium text-ink no-underline shadow-sm hover:bg-accent hover:text-accent-foreground"
            >
              {item.name}
            </PlantHref>
          ))}
        </div>
      )}
    </section>
  )
}

const SPEC_FIELDS: Array<[string, keyof PlantProtectiveFunction]> = [
  ['Demand mode', 'demand_mode'],
  ['Required SIL', 'required_sil'],
  ['Achieved SIL', 'sil'],
  ['Required RRF', 'required_rrf'],
  ['Achieved RRF', 'rrf'],
  ['Achieved PFD avg', 'pfd_avg'],
  ['PFD-implied SIL', 'sil_pfd_avg'],
  ['Response time', 'response_time'],
  ['Process safety time', 'process_safety_time'],
  ['Safe state', 'safe_state'],
  ['Bypass', 'bypass'],
  ['Reset', 'reset'],
  ['Alarm', 'alarm'],
  ['Service', 'service'],
  ['System', 'system_id'],
  ['Logic solver', 'logic_solver']
]

function SpecsSection({
  fn
}: {
  fn: PlantProtectiveFunction | undefined
}): React.ReactElement | null {
  if (!fn) return null
  const specs: Array<[string, string]> = []
  for (const [label, field] of SPEC_FIELDS) {
    const value = fn[field]
    if (typeof value === 'string' && value.trim()) specs.push([label, value])
  }
  if (specs.length === 0) return null
  return (
    <section>
      <SectionHeader label="Specifications" />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(11.25rem,1fr))]">
          {specs.map(([k, v]) => (
            <div key={k}>
              <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
                {k}
              </div>
              <div className="mt-0.5 text-13 font-medium text-ink">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function EmptyCard({ title, hint }: { title: string; hint: string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      <Empty title={title} hint={hint} icon={ShieldIcon} />
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default ProtectiveFunctionDetailPage
